/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "Property requirements" on a lead: the REAL_ESTATE-tagged lead columns
 * (leads.re_*). Shown by LeadDetail and NewLeadModal only when
 * useEditionFields().isFieldVisible('leads', 're_purpose'); the backend drops
 * these fields for tenants without the edition anyway.
 *
 * Read mode shows label/value pairs; edit mode renders inputs and reports
 * each change through onChange(field, value) so the host page keeps its own
 * dirty-field tracking.
 */

import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import { EntityFormFields, formatEnumLabel, formatMoney } from "components/EntityFramework";
import { DetailField, DetailFieldGrid, UiDetailSection } from "components/v2/record/DetailSection";
import { UNIT_CATEGORIES } from "./reEntityConfig";
import { buyerCategoryOptions, defaultCurrency, isAustralia } from "./variant";

export const RE_LEAD_FIELDS = [
  "re_purpose",
  "re_budget_min",
  "re_budget_max",
  "re_bedrooms",
  "re_unit_type",
  "re_preferred_communities",
  "re_completion_status",
  "re_residency_status",
  "re_source_portal",
  "re_project_id",
  "re_unit_id",
];

const PURPOSES = ["BUY", "RENT", "INVEST"];
const BEDROOMS = [
  { value: "studio", label: "Studio" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5+", label: "5+" },
];
const COMPLETION = ["OFF_PLAN", "READY", "ANY"];
const RESIDENCY = ["UAE_NATIONAL", "RESIDENT", "NON_RESIDENT"];
const PORTALS = ["BAYUT", "PROPERTY_FINDER", "DUBIZZLE", "BROKER", "WEBSITE", "WALK_IN", "REFERRAL", "OTHER"];
const PORTALS_AU = [
  { value: "REA", label: "realestate.com.au" },
  { value: "DOMAIN", label: "Domain" },
  { value: "HOMELY", label: "Homely" },
  ...["BROKER", "WEBSITE", "WALK_IN", "REFERRAL", "OTHER"].map((v) => ({ value: v, label: formatEnumLabel(v) })),
];

const FIELDS = [
  { name: "re_purpose", label: "Purpose", type: "select", options: PURPOSES },
  { name: "re_completion_status", label: "Completion", type: "select", options: COMPLETION },
  { name: "re_budget_min", label: "Budget from (AED)", type: "money" },
  { name: "re_budget_max", label: "Budget to (AED)", type: "money" },
  { name: "re_bedrooms", label: "Bedrooms", type: "select", options: BEDROOMS },
  { name: "re_unit_type", label: "Unit type", type: "select", options: UNIT_CATEGORIES },
  { name: "re_preferred_communities", label: "Preferred communities", fullWidth: true },
  { name: "re_residency_status", label: "Residency", type: "select", options: RESIDENCY },
  { name: "re_source_portal", label: "Source portal", type: "select", options: PORTALS },
  {
    name: "re_project_id",
    label: "Project",
    type: "reference",
    reference: { apiPath: "/re/projects" },
    labelField: "re_project_name",
  },
  {
    name: "re_unit_id",
    label: "Unit (optional)",
    type: "reference",
    reference: {
      apiPath: "/re/units",
      params: (values) => (values.re_project_id ? { project_id: values.re_project_id } : {}),
      getLabel: (u) => [u.unit_number, u.project_name].filter(Boolean).join(" · "),
    },
    labelField: "re_unit_number",
  },
];

/** The fields in the tenant's country: Australia asks the buyer category
 * (FIRB) and offers its portals; budgets are in the tenant's currency. */
const fieldsFor = () => {
  const au = isAustralia();
  const cur = defaultCurrency();
  return FIELDS.map((f) => {
    if (f.name === "re_budget_min") return { ...f, label: `Budget from (${cur})` };
    if (f.name === "re_budget_max") return { ...f, label: `Budget to (${cur})` };
    if (au && f.name === "re_residency_status") return { ...f, label: "Buyer", options: buyerCategoryOptions() };
    if (au && f.name === "re_source_portal") return { ...f, options: PORTALS_AU };
    return f;
  });
};

const aed = (value, values) => formatMoney("currency", defaultCurrency())(value, values);

/** Names for the project/unit ids when the lead payload carries only ids. */
function useReferenceNames(projectId, unitId) {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!axiosInstance || !isAuthReady || !isAuthenticated) return undefined;
    let cancelled = false;
    const load = async () => {
      const next = {};
      try {
        if (projectId) next.project = (await axiosInstance.get(`/re/projects/${projectId}`)).data?.name;
      } catch {
        // Unlicensed module or deleted project: fall back to the id.
      }
      try {
        if (unitId) next.unit = (await axiosInstance.get(`/re/units/${unitId}`)).data?.unit_number;
      } catch {
        // as above
      }
      if (!cancelled) setNames(next);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, unitId, axiosInstance, isAuthReady, isAuthenticated]);
  return names;
}

const displayValue = (field, values, names) => {
  const v = values?.[field.name];
  if (v === null || v === undefined || v === "") return "-";
  if (field.name === "re_project_id") return values.re_project_name || names.project || `Project #${v}`;
  if (field.name === "re_unit_id") return values.re_unit_number || names.unit || `Unit #${v}`;
  if (field.type === "money") return aed(v, values);
  if (field.name === "re_bedrooms") return v === "studio" ? "Studio" : v;
  if (field.type === "select") return formatEnumLabel(v);
  return String(v);
};

const LeadPropertyRequirements = ({ values, isEditing, readOnly, onChange, bare }) => {
  const names = useReferenceNames(
    isEditing ? null : values?.re_project_id,
    isEditing ? null : values?.re_unit_id
  );
  const formValues = {};
  fieldsFor().forEach((f) => {
    const v = values?.[f.name];
    formValues[f.name] = v === null || v === undefined ? "" : v;
  });
  formValues.re_project_name = values?.re_project_name || names.project;
  formValues.re_unit_number = values?.re_unit_number || names.unit;

  const body =
    isEditing && !readOnly ? (
      <EntityFormFields
        fields={fieldsFor()}
        values={formValues}
        size="small"
        onChange={(name, value) => {
          let next = value === "" ? null : value;
          if (next !== null && (name === "re_budget_min" || name === "re_budget_max")) {
            const n = Number(next);
            next = Number.isFinite(n) ? n : null;
          }
          onChange(name, next);
          // A unit belongs to one project: changing the project clears it.
          if (name === "re_project_id" && values?.re_unit_id) onChange("re_unit_id", null);
        }}
      />
    ) : (
      <Grid container spacing={3}>
        {fieldsFor().map((field) => (
          <Grid item xs={12} md={field.fullWidth ? 12 : 6} key={field.name}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              <strong>{field.label}:</strong> {displayValue(field, values, names)}
            </MDTypography>
          </Grid>
        ))}
      </Grid>
    );

  if (bare) return body;

  // v2: the shared record section; read view uses the stacked label/value grid.
  const v2Body =
    isEditing && !readOnly ? (
      body
    ) : (
      <DetailFieldGrid>
        {fieldsFor().map((field) => (
          <DetailField key={field.name} label={field.label} fullWidth={Boolean(field.fullWidth)}>
            {/* DetailField draws its own empty dash. */}
            {displayValue(field, values, names) === "-" ? null : displayValue(field, values, names)}
          </DetailField>
        ))}
      </DetailFieldGrid>
    );

  return (
    <UiDetailSection
      title="Property requirements"
      legacy={
        <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: "#ffffff", boxShadow: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
            Property requirements
          </MDTypography>
          {body}
        </MDBox>
      }
    >
      {v2Body}
    </UiDetailSection>
  );
};

LeadPropertyRequirements.propTypes = {
  values: PropTypes.object,
  isEditing: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  bare: PropTypes.bool,
};

LeadPropertyRequirements.defaultProps = {
  values: {},
  isEditing: false,
  readOnly: false,
  onChange: () => {},
  bare: false,
};

export default LeadPropertyRequirements;
